import React, { FC, useMemo, useState, useCallback, useRef, useEffect } from 'react'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import Button from '@mui/material/Button'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import { RichTreeView } from '@mui/x-tree-view/RichTreeView'
import { GenNode } from '../../types/carbon/models/GenNode'
import {
  searchNodes,
  searchNodesKeepingFolders,
  CheckboxState,
} from '../../utils/tree'
import {  
  getNodeID,
} from '../../utils/nodeTypes'
import CustomTreeItem from './Item'
import {
  TreeItemDataContext,
  convertNodesToTreeItems,
  buildTreeItemDataMap,
} from './utils'

const SearchInput: FC<{
  placeholder: string,
  onSearchCommit: (searchTerm: string) => void,
  searchDebounce: number,
  disabled?: boolean,
}> = ({ placeholder, onSearchCommit, searchDebounce, disabled }) => {
  const [localSearchValue, setLocalSearchValue] = useState<string>('')
  const inputRef = useRef<HTMLInputElement>(null)

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearchValue(event.target.value)
  }, [])

  useEffect(() => {
    if (searchDebounce > 0) {
      const debounceTimer = setTimeout(() => {
        onSearchCommit(localSearchValue)
      }, searchDebounce)

      return () => clearTimeout(debounceTimer)
    }
  }, [localSearchValue, onSearchCommit, searchDebounce])

  const handleBlur = useCallback(() => {
    onSearchCommit(localSearchValue)
  }, [localSearchValue, onSearchCommit])

  const handleKeyPress = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      onSearchCommit(localSearchValue)
    }
  }, [localSearchValue, onSearchCommit])

  const handleSearchClick = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.blur()
    }
  }, [])

  const handleClearClick = useCallback(() => {
    setLocalSearchValue('')
    onSearchCommit('')
  }, [onSearchCommit])

  return (
    <TextField
      inputRef={inputRef}
      value={localSearchValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyPress={handleKeyPress}
      placeholder={placeholder}
      size="small"
      fullWidth
      disabled={disabled}
      sx={{ mb: 1 }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {localSearchValue && (
                <Button
                  onClick={handleClearClick}
                  variant="text"
                  size="small"
                  disabled={disabled}
                  aria-label="Clear search"
                  sx={{ minWidth: 'auto', px: 1, color: 'text.secondary', '&:hover': { backgroundColor: 'action.hover', color: 'text.primary' } }}
                >
                  <ClearIcon fontSize="small" />
                </Button>
              )}
              <Button
                onClick={handleSearchClick}
                variant="text"
                size="small"
                disabled={disabled}
                aria-label={placeholder.replace(/\.\.\.$/, '')}
                sx={{ minWidth: 'auto', px: 1, color: 'text.secondary', '&:hover': { backgroundColor: 'action.hover', color: 'text.primary' } }}
              >
                <SearchIcon fontSize="small" />
              </Button>
            </Box>
          </InputAdornment>
        ),
      }}
    />
  )
}

const NodeTree: FC<{
  nodes: GenNode[],
  expanded?: string[],
  selected?: string,
  searchEnabled?: boolean,
  searchPlacement?: 'top' | 'bottom',
  searchPlaceholder?: string,
  searchDebounce?: number,
  /** Search keeps each match inside the folders that lead to it, opened,
   *  instead of listing the matches flat (the construct builder's rail). A
   *  match keeps its own children, so a Variable can still be opened. */
  searchKeepsFolders?: boolean,
  showTooltips?: boolean,
  disabled?: boolean,
  /** Per-node hover text. Wins over showTooltips/label when it returns a value
   *  (#1273 D1: the agent TOC tab shows a table's full path). */
  getNodeTooltip?: (node: GenNode) => React.ReactNode,
  getCheckboxState?: (node: GenNode) => CheckboxState,
  getNodeActions?: (node: GenNode) => React.ReactElement | null,
  onNodeExpandToggle?: (itemId: string, node: GenNode, isExpanded: boolean) => void,
  onNodeSelectToggle?: (itemId: string, node: GenNode, isSelected: boolean) => void,
  onNodeCheckedToggle?: (itemId: string, node: GenNode, isChecked: boolean) => void,
}> = ({
  nodes,
  expanded = [],
  selected,
  searchEnabled = false,
  searchPlacement = 'top',
  searchPlaceholder = "Search tables and folders...",
  searchDebounce = 500,
  searchKeepsFolders = false,
  showTooltips = false,
  disabled = false,
  getNodeTooltip,
  getCheckboxState,
  getNodeActions,
  onNodeExpandToggle,
  onNodeSelectToggle,
  onNodeCheckedToggle,
}) => {
  const [activeSearchTerm, setActiveSearchTerm] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [tooltipsHidden, setTooltipsHidden] = useState(false)
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleScroll = useCallback(() => {
    setTooltipsHidden(true)
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
    scrollTimeoutRef.current = setTimeout(() => setTooltipsHidden(false), 150)
  }, [])

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
    }
  }, [])

  const searching = searchEnabled && !!activeSearchTerm
  const filteredNodes = useMemo(() => {
    if (!searching) return nodes
    return searchKeepsFolders ? searchNodesKeepingFolders(nodes, activeSearchTerm) : searchNodes(nodes, activeSearchTerm)
  }, [nodes, searching, searchKeepsFolders, activeSearchTerm])

  // A flat search can list one node twice, so its ids carry a level suffix.
  // A search that keeps folders cannot, and keeps the tree's own ids, so
  // whatever the parent has open stays open through a search.
  const treeItems = useMemo(() => {
    return convertNodesToTreeItems(filteredNodes, searching && !searchKeepsFolders)
  }, [filteredNodes, searching, searchKeepsFolders])

  const treeItemDataMap = useMemo(() => {
    return buildTreeItemDataMap(treeItems)
  }, [treeItems])

  // While a search keeps folders, the folders leading to the matches open by
  // themselves; one the user closes during that search stays closed.
  const [searchClosed, setSearchClosed] = useState<string[]>([])
  const committedSearchRef = useRef('')
  const searchOpened = useMemo(() => {
    if (!searching || !searchKeepsFolders) return []
    const ids: string[] = []
    const walk = (items: typeof treeItems) => {
      for (const item of items) {
        const type = item.originalNode.type
        if ((type === 'Folder' || type === 'Section') && item.children?.length) {
          if (item.id) ids.push(item.id)
          walk(item.children)
        }
      }
    }
    walk(treeItems)
    return ids
  }, [treeItems, searching, searchKeepsFolders])
  const expandedItems = useMemo(() => {
    if (searchOpened.length === 0) return expanded
    return Array.from(new Set([...expanded, ...searchOpened.filter(id => !searchClosed.includes(id))]))
  }, [expanded, searchOpened, searchClosed])

  // tell the parent that we expanded or collapsed an item
  // this is used to trigger an async data load
  const handleItemExpansionToggle = useCallback((event: React.SyntheticEvent | null, itemId: string, isExpanded: boolean) => {
    if (searchOpened.includes(itemId)) {
      setSearchClosed(prev => (isExpanded ? prev.filter(id => id !== itemId) : [...prev, itemId]))
    }
    if (onNodeExpandToggle) {
      const treeItem = treeItemDataMap.get(itemId)
      if (treeItem?.originalNode) {
        onNodeExpandToggle(itemId, treeItem.originalNode, isExpanded)
      }
    }
  }, [onNodeExpandToggle, treeItemDataMap, searchOpened])

  // tell the parent that an item has been selected
  // this is used to load tables when they are clicked on
  const handleItemSelectionToggle = useCallback((event: React.SyntheticEvent | null, itemId: string, isSelected: boolean) => {
    if (onNodeSelectToggle) {
      const treeItem = treeItemDataMap.get(itemId)
      if (treeItem?.originalNode) {
        onNodeSelectToggle(itemId, treeItem.originalNode, isSelected)
      }
    }
  }, [onNodeSelectToggle, treeItemDataMap])

  const handleSearchCommit = useCallback((searchTerm: string) => {
    if (searchTerm !== committedSearchRef.current) setSearchClosed([])
    committedSearchRef.current = searchTerm
    setActiveSearchTerm(searchTerm)
  }, [])

  if (error) {
    return (
      <Box sx={{ p: 1 }}>
        <span style={{ color: '#d32f2f', fontSize: 12 }}>{error}</span>
      </Box>
    )
  }

  return (
    <TreeItemDataContext.Provider value={{
      treeData: treeItemDataMap,
      getNodeTooltip,
      getCheckboxState,
      getNodeActions,
      onNodeCheckedToggle,
      showTooltips: showTooltips && !tooltipsHidden,
      tooltipsHidden,
      disabled,
    }}>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {searchEnabled && searchPlacement === 'top' && (
          <SearchInput
            placeholder={searchPlaceholder}
            onSearchCommit={handleSearchCommit}
            searchDebounce={searchDebounce}
            disabled={disabled}
          />
        )}
        <Box
          onScroll={handleScroll}
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            minWidth: 0,
          }}
        >
          <ErrorCatcher onError={setError}>
            <RichTreeView
              items={treeItems}
              slots={{
                item: CustomTreeItem,
              }}
              expandedItems={expandedItems}
              selectedItems={selected}
              onItemExpansionToggle={onNodeExpandToggle ? handleItemExpansionToggle : undefined}
              onItemSelectionToggle={onNodeSelectToggle && !disabled ? handleItemSelectionToggle : undefined}
            />
          </ErrorCatcher>
        </Box>
        {searchEnabled && searchPlacement === 'bottom' && (
          <SearchInput
            placeholder={searchPlaceholder}
            onSearchCommit={handleSearchCommit}
            searchDebounce={searchDebounce}
            disabled={disabled}
          />
        )}
      </Box>
    </TreeItemDataContext.Provider>
  )
}

class ErrorCatcher extends React.Component<{ onError: (msg: string) => void, children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: any) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: any) {
    const message = error?.message || 'An error occurred rendering the tree.'
    this.props.onError(message)
  }

  render() {
    if (this.state.hasError) return null
    return this.props.children as any
  }
}

export default NodeTree
